import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Book, ArrowRight, FileText, Folder, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import { useContent } from '../contexts/SupabaseContentContext';
import { format } from 'date-fns';

const Documentation: React.FC = () => {
  const { docPosts, getCategoriesByType } = useContent();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const docsPerPage = 10;

  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'Tài Liệu Hướng Dẫn - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/docs';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const publishedDocs = docPosts.filter(doc => doc.published);
  const categories = getCategoriesByType('doc');
  
  const filteredDocs = publishedDocs.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '' || doc.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredDocs.length / docsPerPage);
  const startIndex = (currentPage - 1) * docsPerPage;
  const endIndex = startIndex + docsPerPage;

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  // Auto-expand all categories initially
  React.useEffect(() => {
    if (categories.length > 0 && expandedCategories.size === 0) {
      setExpandedCategories(new Set(categories.map(cat => cat.id)));
    }
  }, [categories]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Group docs by category for folder structure view
  const docsByCategory = categories.map(category => {
    const categoryDocs = filteredDocs
      .filter(doc => doc.category_id === category.id)
      .sort((a, b) => a.order - b.order);
    
    return {
      category,
      docs: categoryDocs,
      totalDocs: publishedDocs.filter(doc => doc.category_id === category.id).length
    };
  }).filter(group => group.docs.length > 0 || !searchTerm);

  // For pagination when showing search results or specific category
  const shouldShowPagination = (searchTerm || selectedCategory) && filteredDocs.length > docsPerPage;
  const paginatedDocs = shouldShowPagination ? filteredDocs.slice(startIndex, endIndex) : filteredDocs;

  // For folder structure view, apply pagination to each category
  const paginatedDocsByCategory = docsByCategory.map(group => {
    if (searchTerm || selectedCategory) {
      // When filtering, show paginated results
      return {
        ...group,
        docs: shouldShowPagination ? group.docs.slice(startIndex, endIndex) : group.docs
      };
    } else {
      // When showing folder structure, limit each category to 10 docs
      return {
        ...group,
        docs: group.docs.slice(0, 10),
        hasMore: group.docs.length > 10
      };
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Tài Liệu Hướng Dẫn</h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Hướng dẫn chi tiết về cách sử dụng VPS, cài đặt phần mềm và quản trị server
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filter */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Tìm kiếm tài liệu..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Tất cả danh mục</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          {/* Clear filters */}
          {(searchTerm || selectedCategory) && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-gray-600">Bộ lọc đang áp dụng:</span>
              {searchTerm && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                  Tìm kiếm: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedCategory && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  Danh mục: {categories.find(c => c.id === selectedCategory)?.name}
                  <button
                    onClick={() => setSelectedCategory('')}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Xóa tất cả
              </button>
            </div>
          )}
        </div>

        {/* Results Info */}
        {filteredDocs.length > 0 && (
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {searchTerm || selectedCategory ? 'Kết quả tìm kiếm' : 'Cấu trúc tài liệu'}
            </h2>
            <p className="text-gray-600">
              {shouldShowPagination 
                ? `Hiển thị ${startIndex + 1}-${Math.min(endIndex, filteredDocs.length)} trong ${filteredDocs.length} tài liệu`
                : `${filteredDocs.length} tài liệu`
              }
            </p>
          </div>
        )}

        {/* Documentation Content */}
        {filteredDocs.length === 0 ? (
          <div className="text-center py-12">
            <Book className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchTerm || selectedCategory 
                ? 'Không tìm thấy tài liệu nào phù hợp.' 
                : 'Chưa có tài liệu nào.'
              }
            </p>
          </div>
        ) : (searchTerm || selectedCategory) && shouldShowPagination ? (
          // Show paginated search results
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {paginatedDocs.map(doc => {
                const category = categories.find(c => c.id === doc.category_id);
                return (
                  <article key={doc.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                    <div className="p-6">
                      <div className="flex items-center mb-3">
                        <FileText className="h-5 w-5 text-blue-600 mr-2" />
                        <span className="text-sm text-blue-600 font-medium">
                          {category?.name || 'Tài liệu'}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        <Link to={`/docs/${doc.slug}`} className="hover:text-blue-600">
                          {doc.title}
                        </Link>
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">{doc.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          Cập nhật: {format(new Date(doc.updated_at), 'dd/MM/yyyy')}
                        </div>
                        <Link
                          to={`/docs/${doc.slug}`}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          Đọc thêm <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Pagination for search results */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mb-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Trước
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="px-2 py-2 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sau
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            )}
          </>
        ) : (
          // Show folder structure view with limited docs per category
          <div className="space-y-6">
            {paginatedDocsByCategory.map(({ category, docs, totalDocs, hasMore }) => (
              <div key={category.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Category Header - Clickable */}
                <div 
                  className="bg-blue-50 px-6 py-4 border-b border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Folder className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-xl font-bold text-gray-900">{category.name}</h3>
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                        {totalDocs} tài liệu
                      </span>
                    </div>
                    <div>
                      {expandedCategories.has(category.id) ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Category Content - Expandable */}
                {expandedCategories.has(category.id) && (
                  <div className="p-6">
                    {docs.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Không có tài liệu nào trong danh mục này.</p>
                    ) : (
                      <>
                        <ul className="space-y-3">
                          {docs.map(doc => (
                            <li key={doc.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                              <Link 
                                to={`/docs/${doc.slug}`}
                                className="flex items-start group"
                              >
                                <FileText className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                                <div className="flex-1">
                                  <h4 className="text-lg font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                                    {doc.title}
                                  </h4>
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                                    {doc.excerpt}
                                  </p>
                                </div>
                                <div className="ml-4 flex-shrink-0 flex items-center">
                                  <span className="text-xs text-gray-400 mr-2">
                                    {format(new Date(doc.updated_at), 'dd/MM/yyyy')}
                                  </span>
                                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                                </div>
                              </Link>
                            </li>
                          ))}
                        </ul>
                        
                        {/* Show "View All" link if there are more docs */}
                        {hasMore && (
                          <div className="mt-4 text-center">
                            <button
                              onClick={() => setSelectedCategory(category.id)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                            >
                              Xem tất cả {totalDocs} tài liệu trong {category.name} →
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Quick Start Guide */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg text-white p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Mới Bắt Đầu?</h2>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              Hãy bắt đầu với hướng dẫn cơ bản để thiết lập VPS của bạn trong vài phút
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {publishedDocs.length > 0 && (
                <Link
                  to={`/docs/${publishedDocs[0].slug}`}
                  className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center justify-center"
                >
                  <Book className="mr-2 h-5 w-5" />
                  Hướng dẫn cài đặt
                </Link>
              )}
              <a
                href="mailto:support@vmon.vn"
                className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Liên hệ hỗ trợ
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;